import type { DisplayTabEntity, DisplayTabFields } from './clientTypes';
import type { FieldName } from './consts';

export const dataHooks = {
  app: {
    container: 'app.container',
  },
  menus: {
    container: 'menus.container',
    menuState: {
      container: 'menus.menu-state.container',
      content: 'menus.menu-state.content',
      title: 'menus.menu-state.title',
      subtitle: 'menus.menu-state.subtitle',
    },
  },
  menu: {
    container: 'menu.container',
    name: 'menu.name',
    description: 'menu.description',
    sideImage: 'menu.sideImage',
    mobileSideImage: 'menu.mobileSideImage',
  },
  sections: {
    container: 'sections.container',
  },
  section: {
    container: 'section.container',
    name: 'section.name',
    description: 'section.description',
    image: 'section.image',
  },
  items: {
    container: 'items.container',
  },
  item: {
    root: 'item.root',
    container: 'item.container',
    nameContainer: 'item.name-container',
    name: 'item.name',
    description: 'item.description',
    labels: 'item.labels',
    label: 'item.label',
    image: 'item.image',
    price: 'item.price',
    priceContainer: 'item.price-container',
    priceVariants: 'item.priceVariants',
    priceVariantsWithLabel: 'item.price-variants-with-label',
    variant: 'item.variant',
    modifier: (id: string) => `item.modifier.${id}`,
    modifierGroups: 'item.modifierGroups',
    featuredBadge: 'item.featuredBadge',
    reservedImageContainer: 'item.reservedImageContainer',
    discountedPrice: 'item.discountedPrice',
    discountLabel: 'item.discountLabel',
    addToCartContainer: 'item.add-to-cart-container',
  },
  image: {
    image: (dataHook: string) => `${dataHook}.image`,
    clickableImage: (dataHook: string) => `${dataHook}.clickableImage`,
    simpleGalleryModal: (dataHook: string) => `${dataHook}.simpleGalleryModal`,
    badge: (dataHook: string) => `${dataHook}.badge`,
    badgeText: (dataHook: string) => `${dataHook}.badgeText`,
  },
  variant: {
    name: 'variant.name',
    price: 'variant.price',
    discountedPrice: 'variant.discountedPrice',
  },
  modifier: {
    name: 'modifier.name',
    price: 'modifier.price',
    icon: 'modifier.icon',
  },
  modifierGroup: {
    name: (index: number) => `modifierGroup.name.${index}`,
    showButton: 'modifierGroup.showButton',
  },
  navigation: {
    container: 'navigation.container',
    tabs: 'navigation.tabs',
    dropdown: 'navigation.dropdown',
  },
  sectionNavigation: {
    container: 'sectionNavigation.container',
    tabs: 'sectionNavigation.tabs',
  },
  locations: {
    container: 'locations.container',
    dropdown: 'locations.dropdown',
  },
  settings: {
    tabs: 'settings.tabs',
    layoutTab: 'settings.layout-tab',
    manageTab: 'settings.manage-tab',
    designTab: 'settings.design-tab',
    displayTab: 'settings.display-tab',
    styleAITab: 'settings.style-ai-tab',
    menusTab: 'settings.menus-tab',
    drillInItem: (itemName: string) => `settings.drill-in-item.${itemName}`,
    drillInItemTitle: (itemName: string) => `settings.drill-in-item.title.${itemName}`,
    drillInItemIconButton: (itemName: string) => `settings.drill-in-item.icon-button.${itemName}`,
    resetButton: 'settings.reset-button',
    studioChangesBanner: 'settings.studio-changes-banner',
  },
  menuAddOn: {
    settings: {
      tabs: 'menu-add-on.settings.tabs',
      menusTab: 'menu-add-on.settings.menus-tab',
      displayTab: 'menu-add-on.settings.display-tab',
      layoutTab: 'menu-add-on.settings.layout-tab',
      textTab: 'menu-add-on.settings.text-tab',
      designTab: 'menu-add-on.settings.design-tab',
    },
  },
  menuAddOnMenusTab: {
    container: 'menu-add-on.menus-tab.container',
    widgetType: 'menu-add-on.menus-tab.widget-type',
    widgetTypeMenu: 'menu-add-on.menus-tab.widget-type.menu',
    widgetTypeOrdering: 'menu-add-on.menus-tab.widget-type.ordering',
    selectMenu: 'menu-add-on.menus-tab.select-menu',
    itemsToShow: 'menu-add-on.menus-tab.items-to-show',
    selectSection: 'menu-add-on.menus-tab.select-section',
    selectRibbon: 'menu-add-on.menus-tab.select-ribbon',
    createMenuButton: 'menu-add-on.menus-tab.create-menu-button',
    manageMenuButton: 'menu-add-on.menus-tab.manage-menu-button',
    createItemButton: 'menu-add-on.menus-tab.create-item-button',
    selectItemsButton: 'menu-add-on.menus-tab.select-items-button',
    selectedItemsList: 'menu-add-on.menus-tab.selected-items-list',
    selectedItemRow: (itemId: string) => `menu-add-on.menus-tab.selected-items-list.row.${itemId}`,
    clearSelectedItemsButton: 'menu-add-on.menus-tab.clear-selected-items-button',
  },
  menuAddOnDisplayTab: {
    container: 'menu-add-on.display-tab.container',
    viewFullMenuCheckbox: 'menu-add-on.display-tab.view-full-menu-checkbox',
    addToCartCheckbox: 'menu-add-on.display-tab.add-to-cart-checkbox',
  },
  menuAddOnWidget: {
    orderingBanner: 'menu-add-on.widget.ordering-banner',
    orderingBannerChangeButton: 'menu-add-on.widget.ordering-banner.change-button',
    orderingBannerViewCartButton: 'menu-add-on.widget.ordering-banner.view-cart-button',
    orderingPageConflict: 'menu-add-on.widget.ordering-page-conflict',
    sliderPreviousButton: 'menu-add-on.widget.slider.previous-button',
    sliderNextButton: 'menu-add-on.widget.slider.next-button',
  },
  menuAddOnLayoutTab: {
    container: 'menu-add-on.layout-tab.container',
    row: (id: string) => `menu-add-on.layout-tab.row.${id}`,
    headerAlignment: 'menu-add-on.layout-tab.header-alignment',
    itemsSettings: 'menu-add-on.layout-tab.items-settings',
    maxItemsPerRow: 'menu-add-on.layout-tab.items.max-items-per-row',
    itemsDisplayMode: 'menu-add-on.layout-tab.items.display-mode',
    itemsDisplayModeGrid: 'menu-add-on.layout-tab.items.display-mode.grid',
    itemsDisplayModeSlider: 'menu-add-on.layout-tab.items.display-mode.slider',
    itemsPerLoad: 'menu-add-on.layout-tab.items.items-per-load',
    itemsColumnGap: 'menu-add-on.layout-tab.items.column-gap',
    itemsRowGap: 'menu-add-on.layout-tab.items.row-gap',
    cardAlignment: 'menu-add-on.layout-tab.items.card-alignment',
    itemInfoSidePadding: 'menu-add-on.layout-tab.items.item-info-side-padding',
    itemVerticalPadding: 'menu-add-on.layout-tab.items.item-vertical-padding',
    itemInfoAlignment: 'menu-add-on.layout-tab.items.item-info-alignment',
    itemImageSettings: 'menu-add-on.layout-tab.items.image-settings',
    itemImageRatio: 'menu-add-on.layout-tab.items.image.ratio',
    itemImageResizeMode: 'menu-add-on.layout-tab.items.image.resize-mode',
    itemImageVerticalPadding: 'menu-add-on.layout-tab.items.image.vertical-padding',
    itemImageSidePadding: 'menu-add-on.layout-tab.items.image.side-padding',
    featuredBadgePlacement: 'menu-add-on.layout-tab.items.ribbon-placement',
    resetItemsSettings: 'menu-add-on.layout-tab.items.reset',
    backgroundPadding: 'menu-add-on.layout-tab.items.background-padding',
    backgroundHorizontalPadding: 'menu-add-on.layout-tab.items.background-padding.horizontal',
    backgroundVerticalPadding: 'menu-add-on.layout-tab.items.background-padding.vertical',
    sliderArrowDistance: 'menu-add-on.layout-tab.items.slider-arrow-distance',
    sliderArrowPosition: 'menu-add-on.layout-tab.items.slider-arrow-position',
    sliderArrowPositionInside: 'menu-add-on.layout-tab.items.slider-arrow-position.inside',
    sliderArrowPositionOutside: 'menu-add-on.layout-tab.items.slider-arrow-position.outside',
    sliderNavigationArrows: 'menu-add-on.layout-tab.items.slider-navigation-arrows',
    viewFullMenuButtonAlignment: 'menu-add-on.layout-tab.view-full-menu-button-alignment',
    addToCartButtonAlignment: 'menu-add-on.layout-tab.add-to-cart-button-alignment',
    loadMoreButtonAlignment: 'menu-add-on.layout-tab.load-more-button-alignment',
  },
  menuAddOnTextTab: {
    container: 'menu-add-on.text-tab.container',
    titleField: 'menu-add-on.text-tab.title-field',
    subtitleField: 'menu-add-on.text-tab.subtitle-field',
    addToCartField: 'menu-add-on.text-tab.add-to-cart-field',
    viewFullMenuField: 'menu-add-on.text-tab.view-full-menu-field',
    resetButton: 'menu-add-on.text-tab.reset-button',
  },
  menuAddOnDesignTab: {
    container: 'menu-add-on.design-tab.container',
    row: (id: string) => `menu-add-on.design-tab.row.${id}`,
  },
  menuAddOnSelectItemsModal: {
    modal: 'menu-add-on.select-items-modal',
    search: 'menu-add-on.select-items-modal.search',
    sectionHeader: (sectionId: string) => `menu-add-on.select-items-modal.section-header.${sectionId}`,
    item: (itemId: string) => `menu-add-on.select-items-modal.item.${itemId}`,
  },
  fields: {
    thumbnailPicker: {
      fieldSet: (field: string) => `fields.thumbnailPicker.fieldSet.${field}`,
      thumbnail: (field: string, value: number) => `fields.thumbnailPicker.thumbnail.${field}.${value}`,
    },
    presetSelect: {
      fieldSet: (index: number) => `fields.presetSelect.fieldSet.${index}`,
      thumbnail: (field: string) => `fields.presetSelect.thumbnail.${field}`,
    },
    expandTypeSelect: {
      fieldSet: 'fields.expandType.fieldSet',
      thumbnail: (index: number) => `fields.expandType.thumbnail.${index}`,
    },
    overflowField: {
      fieldSet: `fields.overflowField.fieldSet`,
      thumbnail: (field: string) => `fields.overflowField.thumbnail.${field}`,
    },
    listViewField: {
      fieldSet: `fields.listViewField.fieldSet`,
      thumbnail: (field: string) => `fields.listViewField.thumbnail.${field}`,
    },
    textStyleField: {
      fieldSet: `fields.textStyleField.fieldSet`,
      toggleButton: (field: string) => `fields.textStyleField.toggleButton.${field}`,
    },
    borderPicker: {
      formField: (field?: FieldName) => `fields.borderPicker.formField.${field}`,
      baseBorderPicker: (position: string, field?: FieldName) =>
        `fields.borderPicker.baseBorderPicker.${position}.${field}`,
      baseBorderInput: (field?: FieldName) => `fields.borderPicker.baseBorderInput.${field}`,
      baseColorPicker: (field?: FieldName) => `fields.borderPicker.baseColorPicker.${field}`,
      toggleButton: (field?: FieldName) => `fields.borderPicker.toggleButton.${field}`,
    },
    slider: {
      fieldSet: (field?: FieldName) => `fields.slider.${field}.fieldSet`,
      slider: (field?: FieldName) => `fields.slider.${field}.slider`,
      input: (field?: FieldName) => `fields.slider.${field}.input`,
    },
    numberInput: {
      label: (field: FieldName) => `fields.numberInput.${field}.label`,
      input: (field: FieldName) => `fields.numberInput.maxItems.${field}.input`,
    },
    toggleSwitch: {
      text: (field: FieldName) => `fields.toggleSwitch.${field}.text`,
      toggle: (field: FieldName) => `fields.toggleSwitch.${field}.toggle`,
    },
    radioGroup: {
      field: (field: string) => `fields.radioGroup.field.${field}`,
      group: (field: string) => `fields.radioGroup.${field}`,
      radio: (index: number, field: string) => `fields.radio.${field}.${index}`,
    },
    backButton: {
      button: 'fields.backButton.button',
    },
    cornerRadius: {
      formField: (field: string) => `fields.cornerRadius.formField.${field}`,
      cornerRadiusInput: (field: string) => `fields.cornerRadius.cornerRadiusInput.${field}`,
    },
    dropdown: {
      formField: (field: string) => `fields.dropdown.formField.${field}`,
      dropdown: (field: string) => `fields.dropdown.dropdown.${field}`,
    },
    segmentedToggle: {
      formField: (field: string) => `fields.segmentedToggle.formField.${field}`,
      toggle: (field: string) => `fields.segmentedToggle.toggle.${field}`,
      option: (value: string) => `fields.segmentedToggle.option.${value}`,
    },
    fontAndColorPicker: (field: string) => `fields.fontAndColorPicker.${field}`,
    opacityColorPicker: (field: string) => `fields.opacityColorPicker.${field}`,
    colorPicker: (field: string) => `fields.colorPicker.${field}`,
  },
  manageTab: {
    container: 'settings.manage-tab.container',
    emptyState: 'settings.manage-tab.empty-state',
    manageMenusButton: 'settings.manage-tab.manage-menus-button',
    addMenuPageButton: 'settings.manage-tab.add-menu-page-button',
    subtitle: 'settings.manage-tab.subtitle',
  },
  styleAiTab: {
    userInstructionsScreen: 'style-ai-tab.user-instructions.screen',
    userInstructionsDescription: 'style-ai-tab.user-instructions.description',
    descriptionInputTitle: 'style-ai-tab.user-instructions.input-title',
    descriptionInput: 'style-ai-tab.user-instructions.input',
    generateButton: 'style-ai-tab.user-instructions.generate-button',
    siteThemeLabel: 'style-ai-tab.user-instructions.site-theme.label',
    siteThemeToggle: 'style-ai-tab.user-instructions.site-theme.toggle',
    previewScreenContainer: 'style-ai-tab.preview-screen.container',
    progressBar: 'style-ai-tab.preview-screen.progress-bar',
    progressBarLabel: 'style-ai-tab.preview-screen.progress-bar-label',
    skeletonGroup: 'style-ai-tab.preview-screen.skeleton-group',
    previewTitle: 'style-ai-tab.preview-screen.title',
    previewContainer: 'style-ai-tab.preview-screen.container',
    revertButton: 'style-ai-tab.preview-screen.revert-button',
  },
  menusTab: {
    container: 'settings.menus-tab.container',
    title: 'settings.menus-tab.title',
    displayOptions: 'settings.menus-tab.display-options',
    displayAll: 'settings.menus-tab.display-options.all',
    displaySpecific: 'settings.menus-tab.display-options.specific',
    menusList: 'settings.menus-tab.menus-list',
    menuListRow: (index: string) => `settings.menus-tab.menus-list.row.${index}`,
    locationListRow: (index: string) => `settings.menus-tab.locations-list.row.${index}`,
    menuName: (index: string) => `settings.menus-tab.menus-list.menu-name.${index}`,
    menuListRowActions: (index: string) => `settings.menus-tab.menus-list.actions.${index}`,
    draftMenuIcon: (index: string) => `settings.menus-tab.menus-list.draft-menu.icon.${index}`,
    manageMenus: 'settings.menus-tab.manage-menus',
    manageMenusLink: 'settings.menus-tab.manage-menus.link',
  },
  displayTab: {
    sectionsTabs: {
      checkbox: 'settings.display-tab.sections.checkbox',
      label: 'settings.display-tab.sections.checkbox.label',
      infoIcon: 'settings.display-tab.sections.checkbox.infoIcon',
    },
    priceInfoIcon: 'settings.display-tab.priceInfoIcon',
    container: 'settings.display-tab.container',
    multiMenusTitle: 'settings.display-tab.multiMenusTitle',
    multiMenusTabsCheckbox: 'settings.display-tab.tabs.checkbox',
    columnDividerCheckbox: 'settings.display-tab.column-divider.checkbox',
    formFieldLabel: (entity: DisplayTabFields) => `settings.display-tab.formField-label.${entity}`,
    nameCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.name.checkbox.${entity}`,
    iconCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.icon.checkbox.${entity}`,
    descriptionCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.description.checkbox.${entity}`,
    priceCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.price.checkbox.${entity}`,
    currencyCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.currency.checkbox.${entity}`,
    labelsCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.labels.checkbox.${entity}`,
    labelsNameCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.labelsName.checkbox.${entity}`,
    labelsIconCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.labelsIcon.checkbox.${entity}`,
    imageCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.image.checkbox.${entity}`,
    dividersCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.dividers.checkbox.${entity}`,
    columnDividersCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.column-dividers.checkbox.${entity}`,
    gridDividersCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.grid-dividers.checkbox.${entity}`,
    highlightedItemCheckbox: (entity: DisplayTabEntity) => `settings.display-tab.highlighted-item.checkbox.${entity}`,
    dotsCheckbox: 'settings.display-tab.dots.checkbox',
    modifierGroupNameCheckbox: 'settings.display-tab.modifier-group.checkbox',
    pricingOptionsRadioGroup: 'settings.display-tab.section.pricingOptions.radio-group',
    pricingOptionsShowZeroText: 'settings.display-tab.section.pricingOptions.show-zero.text',
    pricingOptionsShowZeroToggle: 'settings.display-tab.section.pricingOptions.show-zero.toggle',
    pricingOptionsSpecificMenusRadioText: 'settings.display-tab.section.pricingOptions.radio.specific-menus.text',
    pricingOptionsAllMenusRadioText: 'settings.display-tab.section.pricingOptions.radio.all-menus.text',
    imagePlaceholderRadioGroup: 'settings.display-tab.section.imagePlaceholder.radio-group',
    imagePlaceholderShowPlaceholderText: 'settings.display-tab.section.imagePlaceholder.show-placeholder.text',
    imagePlaceholderShowPlaceholderToggle: 'settings.display-tab.section.imagePlaceholder.show-placeholder.toggle',
    imagePlaceholderSpecificMenusRadioText: 'settings.display-tab.section.imagePlaceholder.radio.specific-menus.text',
    imagePlaceholderAllMenusRadioText: 'settings.display-tab.section.imagePlaceholder.radio.all-menus.text',
    imageViewerChangeActionButton: 'settings.display-tab.section.image-viewer.change-action.button',
    imageViewerChangeActionTooltip: 'settings.display-tab.section.image-viewer.change-action.tooltip',
    imageViewerResetActionButton: 'settings.display-tab.section.image-viewer.reset-action.button',
    imageViewerResetActionTooltip: 'settings.display-tab.section.image-viewer.reset-action.tooltip',
    imageViewer: 'settings.display-tab.section.image-viewer',
    sideImageTitle: 'settings.display-tab.section.side-image.title',
    sideImageShowText: 'settings.display-tab.section.side-image.show-text',
    sideImageShowToggle: 'settings.display-tab.section.side-image.show-toggle',
    menuCheckbox: (id: string) => `settings.display-tab.menus.checkboxes.${id}`,
    imageExpandedModeCheckbox: (entity: DisplayTabEntity) =>
      `settings.display-tab.image.expanded-mode.checkbox.${entity}`,
    imageMultiImagesGalleryCheckbox: (entity: DisplayTabEntity) =>
      `settings.display-tab.image.multi-images-gallery.checkbox.${entity}`,
    imageMultiImagesGalleryInfoIcon: 'settings.display-tab.image.multi-images-gallery.info-icon',
  },
  layoutTab: {
    container: 'settings-tab.layout-tab.container',
    navigation: {
      sectionHeader: 'settings-tab.layout-tab.navigation.header',
      drillInItem: 'settings-tab.layout-tab.navigation.drill-in-item',
    },
    unifiedNavigation: {
      sectionHeader: 'settings-tab.layout-tab.unified-navigation.header',
    },
    sectionNavigation: {
      sectionHeader: 'settings-tab.layout-tab.section-navigation.header',
    },
    locations: {
      sectionHeader: 'settings-tab.layout-tab.locations.header',
    },
  },
  designTab: {
    container: 'settings-tab.design-tab.container',
    navigation: {
      tabsHeader: 'settings-tab.design-tab.navigation.tabs-header',
      tabStatesHeader: 'settings-tab.design-tab.navigation.tab-header',
      statesTabs: 'settings-tab.design-tab.navigation.states-tabs',
      dropdownHeader: 'settings-tab.design-tab.navigation.dropdown-header',
    },
    sectionNavigation: {
      tabStatesHeader: 'settings-tab.design-tab.section-navigation.tab-header',
      statesTabs: 'settings-tab.design-tab.section-navigation.states-tabs',
    },
    unifiedNavigation: {
      header: 'settings-tab.design-tab.unified-navigation.header',
    },
    menus: {
      header: 'settings-tab.design-tab.menus.header',
    },
    sections: {
      header: 'settings-tab.design-tab.sections.header',
    },
    columnDivider: {
      header: 'settings-tab.design-tab.column-divider.header',
    },
    items: {
      header: 'settings-tab.design-tab.items.header',
    },
    variants: {
      header: 'settings-tab.design-tab.variants.header',
    },
    modifiers: {
      header: 'settings-tab.design-tab.modifiers.header',
      buttonHeader: 'settings-tab.design-tab.modifiers.button-header',
      statesTabs: 'settings-tab.design-tab.modifiers.states-tabs',
    },
    dots: {
      header: 'settings-tab.design-tab.dots.header',
    },
    labels: {
      header: 'settings-tab.design-tab.labels.header',
      iconHeader: 'settings-tab.design-tab.labels.iconHeader',
      spacingHeader: 'settings-tab.design-tab.labels.spacingHeader',
    },
    featuredItem: {
      header: 'settings-tab.design-tab.featured-item.header',
      info: 'settings-tab.design-tab.featured-item.info',
      elementsHeader: 'settings-tab.design-tab.featured-item.elements-header',
    },
    multiImages: {
      badge: 'settings-tab.design-tab.multi-images.badge',
      gallery: 'settings-tab.design-tab.multi-images.gallery',
    },
  },
};

export const getDataHook = (dataHook: string) => `[data-hook="${dataHook}"]`;
